import React, { useState, useEffect, useContext } from "react";
import {
  Settings,
  ShieldAlert,
  ShieldCheck,
  CalendarDays,
  MessageSquare,
  Save,
  CheckCircle2,
  Wrench,
} from "lucide-react";
import AuthContext from "../../../context/AuthContext";
import api from "../../../services/AxiosConnection";

function MaintenanceManagement() {
  // 1. DECLARE ALL HOOKS UNCONDITIONALLY AT THE VERY TOP
  const { selectedSite } = useContext(AuthContext);
  
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [currentState, setCurrentState] = useState(null);
  const [isActiveMaintenance, setIsActiveMaintenance] = useState(false);
  
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [reason, setReason] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch Applications
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!selectedSite || user?.role !== "Super Admin") return; // Early exit inside the hook is fine

    const fetchApplications = async () => {
      try {
        const response = await api.get("/diagnostic/getApplications", {
          params: { siteDb: selectedSite },
        });
        const apps = response.data.applications || [];
        setApplications(apps);
        if (apps.length > 0 && !selectedAppId) {
          setSelectedAppId(apps[0].appId);
        }
      } catch (error) {
        console.error("Error fetching applications:", error);
      }
    };
    fetchApplications();
  }, [selectedSite, selectedAppId]);

  // Fetch Current State
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!selectedSite || !selectedAppId || user?.role !== "Super Admin") return; // Early exit inside the hook is fine

    const fetchCurrentState = async () => {
      try {
        const response = await api.get("/diagnostic/getCurrentMaintenanceState", {
          params: { siteDb: selectedSite, appId: selectedAppId },
        });
        
        const state = response.data.maintenanceState;
        const isActive = response.data.isActiveMaintenance;
        
        setCurrentState(state);
        setIsActiveMaintenance(isActive);
        
        if (state) {
          setIsMaintenance(state.IsMaintenance);
          setReason(state.Reason || "");
          setFromDate(state.FromDate ? new Date(state.FromDate).toISOString().slice(0, 16) : "");
          setToDate(state.ToDate ? new Date(state.ToDate).toISOString().slice(0, 16) : "");
        } else {
          resetForm();
        }
      } catch (error) {
        console.error("Error fetching current state:", error);
      }
    };
    fetchCurrentState();
  }, [selectedSite, selectedAppId]);

  const resetForm = () => {
    setIsMaintenance(false);
    setReason("");
    setFromDate("");
    setToDate("");
  };

  const handleInsert = async (e) => {
    e.preventDefault();
    if (!selectedAppId || !fromDate || !toDate) {
      alert("Please select an application and provide valid dates.");
      return;
    }

    setLoading(true);
    setSuccessMessage("");
    
    try {
      await api.post("/diagnostic/createMaintenanceRecord", {
        siteDb: selectedSite,
        appId: selectedAppId,
        isMaintenance: isMaintenance,
        reason: reason,
        fromDate: fromDate,
        toDate: toDate,
      });
      
      setSuccessMessage("New maintenance record added successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      const response = await api.get("/diagnostic/getCurrentMaintenanceState", {
        params: { siteDb: selectedSite, appId: selectedAppId },
      });
      setCurrentState(response.data.maintenanceState);
      setIsActiveMaintenance(response.data.isActiveMaintenance);

    } catch (error) {
      console.error("Error inserting maintenance record:", error);
      alert("Failed to insert maintenance record.");
    } finally {
      setLoading(false);
    }
  };

  const isCurrentlyUnderMaintenance = isActiveMaintenance;

  // 2. GUARD CLAUSE PLACED AFTER ALL HOOKS AND FUNCTIONS
  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.role !== "Super Admin") {
    return null; 
  }

  // 3. RETURN JSX
  return (
    <div className="p-3 max-w-5xl mx-auto space-y-4">
      {/* Success Message Banner */}
      {successMessage && (
        <div className="flex items-center justify-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl shadow-sm animate-pulse">
          <CheckCircle2 className="text-emerald-500" size={18} />
          <span className="text-sm font-semibold text-emerald-700">{successMessage}</span>
        </div>
      )}

      {/* Select Application Dropdown */}
      <div className="flex justify-center">
        <select
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
          className="w-80 p-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300"
        >
          {applications.length === 0 && <option value="">No Applications Found</option>}
          {applications.map((app) => (
            <option key={app.appId} value={app.appId}>
              {app.appName}
            </option>
          ))}
        </select>
      </div>

      {/* Configuration Cards */}
      <div className="flex flex-col lg:flex-row gap-4">
        
        {/* Card 1: Current Live Status Display (Strictly 2 States) */}
        <div className="flex-1">
          <div className="h-full bg-gradient-to-br from-white to-gray-50 p-4 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col min-h-[280px]">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg shadow-sm">
                <Settings className="text-white" size={18} />
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-bold text-gray-800">Current System State</h3>
                <p className="text-xs text-gray-500 mt-0.5">Live database status</p>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col justify-center">
              <div className={`relative rounded-xl p-6 border-2 transition-all duration-500 flex flex-col items-center justify-center gap-3
                ${isCurrentlyUnderMaintenance 
                  ? "border-rose-200 bg-gradient-to-br from-rose-50 to-white" 
                  : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"}
              `}>
                <div className={`p-4 rounded-full shadow-lg transition-all duration-500 
                  ${isCurrentlyUnderMaintenance 
                    ? "bg-gradient-to-br from-rose-500 to-red-500" 
                    : "bg-gradient-to-br from-emerald-500 to-green-500"}`}
                >
                  {isCurrentlyUnderMaintenance ? <ShieldAlert className="text-white" size={32} /> : <ShieldCheck className="text-white" size={32} />}
                </div>
                
                <div className="text-center">
                  <span className={`text-lg font-bold tracking-wide transition-colors duration-500 
                    ${isCurrentlyUnderMaintenance ? "text-rose-600" : "text-emerald-600"}`}
                  >
                    {isCurrentlyUnderMaintenance ? "UNDER MAINTENANCE" : "RUNNING"}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-auto pt-3 border-t border-gray-100">
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span>Running</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                  <span>Under Maintenance</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Add New Record Form */}
        <div className="flex-1">
          <div className="h-full bg-gradient-to-br from-white to-gray-50 p-4 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col min-h-[280px]">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg shadow-sm">
                <Wrench className="text-white" size={18} />
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-bold text-gray-800">Add New Schedule</h3>
                <p className="text-xs text-gray-500 mt-0.5">Inserts a new history log</p>
              </div>
            </div>
            
            <form onSubmit={handleInsert} className="flex-1 flex flex-col gap-3">
              {/* Toggle for New Entry */}
              <div 
                onClick={() => setIsMaintenance(!isMaintenance)}
                className={`flex items-center justify-between p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-300 ${isMaintenance ? 'border-rose-200 bg-rose-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <span className="text-xs font-medium text-gray-700">Set as Maintenance?</span>
                <div className={`w-10 h-5 rounded-full relative transition-colors duration-300 ${isMaintenance ? 'bg-rose-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${isMaintenance ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                </div>
              </div>

              {/* Reason Input */}
              <div className="relative">
                <MessageSquare className="absolute top-2.5 left-2.5 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="Reason for maintenance..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white/70 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                />
              </div>

              {/* Date Inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From Date</label>
                  <div className="relative">
                    <CalendarDays className="absolute top-2.5 left-2.5 text-gray-400" size={14} />
                    <input
                      type="datetime-local"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      required
                      className="w-full pl-8 pr-2 py-2 text-xs border border-gray-200 rounded-lg bg-white/70 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">To Date</label>
                  <div className="relative">
                    <CalendarDays className="absolute top-2.5 left-2.5 text-gray-400" size={14} />
                    <input
                      type="datetime-local"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      required
                      className="w-full pl-8 pr-2 py-2 text-xs border border-gray-200 rounded-lg bg-white/70 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1"></div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-lg text-sm font-bold text-white shadow-md transition-all duration-300 flex items-center justify-center gap-2
                  ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 hover:shadow-lg"}
                `}
              >
                <Save size={16} />
                {loading ? "Saving..." : "Submit"}
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
}

export default MaintenanceManagement;